import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import patientRecordService, {
  PatientCareProviderDto,
  PatientLinkDto,
  PatientProfileFields,
  PatientRecordResponse,
  UpdateProfileFieldsBody,
  UpsertCareProviderBody,
  UpsertPatientLinkBody,
} from '../../services/patientRecordService';

/** One key for the whole record surface — the read, the read-only gate and
 * every mutation below invalidate through this so none of them can disagree. */
export const patientRecordQueryKey = (pharmacyId?: string, patientId?: string) =>
  ['patient-record', pharmacyId, patientId] as const;

/**
 * A 4xx here is a settled answer (a patient this role may not view, or —
 * until the endpoint ships — a route that does not exist yet), not a blip.
 * Retrying it buys nothing and only delays the fallback below.
 */
function isDefinitiveClientError(error: unknown): boolean {
  const status = (error as any)?.response?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

/**
 * PRX-80 — the composite Patient 360 record: presence, the one read-only
 * flag/reason, the PHOX-owned profile fields, care providers, links,
 * specialty drugs, the permanent note and the Zoho staging flag.
 *
 * Disabled until both ids are present — a request keyed on `undefined` would
 * cache one patient's record under every other patient's key.
 *
 * The endpoint this calls (Task 1) is built in parallel with this hook. A
 * failed read here is read by `Patient360ReadOnlyContext.deriveReadOnlyFromRecordQuery`
 * as "leave the page exactly as writable as it is today" — this hook itself
 * does no rewriting or toasting of that error; it is surfaced verbatim on
 * `.error`, same as every other query in this codebase.
 */
export function usePatientRecord(pharmacyId?: string, patientId?: string) {
  return useQuery({
    queryKey: patientRecordQueryKey(pharmacyId, patientId),
    queryFn: (): Promise<PatientRecordResponse> =>
      patientRecordService.getPatientRecord(pharmacyId as string, patientId as string),
    enabled: !!pharmacyId && !!patientId,
    staleTime: 60_000,
    retry: (failureCount, error) => (isDefinitiveClientError(error) ? false : failureCount < 1),
  });
}

/** A record write's variables: the body plus the patient it is for, fixed when
 * the caller calls `mutate`/`mutateAsync`. */
interface PatientRecordMutationVariables<TBody> {
  pharmacyId?: string;
  patientId?: string;
  body: TBody;
}

/**
 * One record write — the PATCH/POST/DELETE, and the record invalidation that
 * follows it — bound to the patient the call was MADE for.
 *
 * PRX-80 Task 9 (C1) fixed the invalidation; the final review (I2) found the
 * WRITE itself still reading `pharmacyId`/`patientId` out of the hook's
 * closure. `useMutation` rebinds its config onto the latest render, and
 * TanStack v5 pushes that rebinding down onto a mutation that is still PENDING
 * (`MutationObserver.setOptions` → `Mutation.setOptions`) — so a save that has
 * not reached the server yet when the page moves to another patient (paused on
 * a lost connection, or simply still in flight) sent THAT patient's ids: a care
 * provider, a caregiver link or a profile field written onto the wrong chart.
 *
 * The fix is the shape `useUpdatePatientStatus` already uses
 * (`usePatientStatus.ts`): the ids travel in the mutation's OWN variables,
 * captured by `mutate`/`mutateAsync` below when the caller calls them, and
 * both the `mutationFn` and `onSuccess` read them from there. Callers still
 * pass just the body, and their own per-call `onSuccess`/`onError` still run
 * — after the invalidation, because the config `onSuccess` returns its
 * promise.
 */
function usePatientRecordMutation<TData, TBody>(
  write: (pharmacyId: string, patientId: string, body: TBody) => Promise<TData>,
  pharmacyId: string | undefined,
  patientId: string | undefined,
) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (vars: PatientRecordMutationVariables<TBody>): Promise<TData> =>
      write(vars.pharmacyId as string, vars.patientId as string, vars.body),
    onSuccess: (_data, vars) =>
      queryClient.invalidateQueries({ queryKey: patientRecordQueryKey(vars.pharmacyId, vars.patientId) }),
  });

  return {
    ...mutation,
    mutate: (body: TBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ pharmacyId, patientId, body }, options),
    mutateAsync: (body: TBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync({ pharmacyId, patientId, body }, options),
  };
}

/**
 * The Patient info tab's profile-fields save. Invalidates the record read on
 * success so every card reading from it (the tab, the read-only banner)
 * repaints from the server's answer rather than from what the form sent.
 */
export function useUpdateProfileFields(pharmacyId?: string, patientId?: string) {
  return usePatientRecordMutation<PatientProfileFields, UpdateProfileFieldsBody>(
    (forPharmacyId, forPatientId, body) =>
      patientRecordService.updateProfileFields(forPharmacyId, forPatientId, body),
    pharmacyId,
    patientId,
  );
}

/**
 * Upsert/update/delete a care provider. `upsert` is the create route — the
 * server upserts on `(patient_id, role)`, so posting a second family doctor
 * replaces the first rather than adding a row.
 */
export function useCareProviderMutations(pharmacyId?: string, patientId?: string) {
  const upsert = usePatientRecordMutation<PatientCareProviderDto, UpsertCareProviderBody>(
    (forPharmacyId, forPatientId, body) =>
      patientRecordService.upsertCareProvider(forPharmacyId, forPatientId, body),
    pharmacyId,
    patientId,
  );

  const update = usePatientRecordMutation<
    PatientCareProviderDto,
    { careProviderId: string; body: UpsertCareProviderBody }
  >(
    (forPharmacyId, forPatientId, input) =>
      patientRecordService.updateCareProvider(forPharmacyId, forPatientId, input.careProviderId, input.body),
    pharmacyId,
    patientId,
  );

  const remove = usePatientRecordMutation<void, string>(
    (forPharmacyId, forPatientId, careProviderId) =>
      patientRecordService.deleteCareProvider(forPharmacyId, forPatientId, careProviderId),
    pharmacyId,
    patientId,
  );

  return { upsert, update, remove };
}

/** Create/update/delete a patient link (parcel-goes-with / relative / caregiver). */
export function usePatientLinkMutations(pharmacyId?: string, patientId?: string) {
  const create = usePatientRecordMutation<PatientLinkDto, UpsertPatientLinkBody>(
    (forPharmacyId, forPatientId, body) =>
      patientRecordService.createPatientLink(forPharmacyId, forPatientId, body),
    pharmacyId,
    patientId,
  );

  const update = usePatientRecordMutation<PatientLinkDto, { linkId: string; body: UpsertPatientLinkBody }>(
    (forPharmacyId, forPatientId, input) =>
      patientRecordService.updatePatientLink(forPharmacyId, forPatientId, input.linkId, input.body),
    pharmacyId,
    patientId,
  );

  const remove = usePatientRecordMutation<void, string>(
    (forPharmacyId, forPatientId, linkId) =>
      patientRecordService.deletePatientLink(forPharmacyId, forPatientId, linkId),
    pharmacyId,
    patientId,
  );

  return { create, update, remove };
}
